import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '~/components/button';

export const Route = createFileRoute('/_authed/biometrics/')({
  component: BiometricsIndex,
});

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return (await res.json()) as T;
}

interface AcuteVerdict {
  strain: boolean;
  today: number | null;
  baselineMean: number | null;
  baselineSd: number | null;
  baselineN: number;
  thresholdHigh: number | null;
}

interface Source {
  provider: string;
  connectionType: string;
  connectedAt: number;
  lastSyncAt: number | null;
  lastError: string | null;
}

interface Summary {
  sources: Source[];
  readingsCount: number;
}

interface Digest {
  weeks: Array<{
    week: string;
    hrvMean?: number;
    sleepMinMean?: number;
    rhrMean?: number;
    stepsSum?: number;
    entryCount: number;
  }>;
  digest: string;
}

const PASTE_EXAMPLE = `{
  "provider": "manual",
  "readings": [
    { "day": "2026-05-01", "metric": "hrv_rmssd", "value": 52, "unit": "ms" },
    { "day": "2026-05-02", "metric": "hrv_rmssd", "value": 48, "unit": "ms" }
  ]
}`;

function BiometricsIndex() {
  const acute = useQuery({
    queryKey: ['biometrics-acute'],
    queryFn: () => fetchJson<AcuteVerdict>('/api/biometrics/acute'),
  });
  const summary = useQuery({
    queryKey: ['biometrics-summary'],
    queryFn: () => fetchJson<Summary>('/api/biometrics/summary'),
  });
  const digest = useMutation({
    mutationFn: () => fetchJson<Digest>('/api/biometrics/digest/weekly', { method: 'POST' }),
  });

  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);
  const importMutation = useMutation({
    mutationFn: (body: unknown) =>
      fetchJson<{ ingested: number }>('/api/biometrics/import', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      summary.refetch();
      acute.refetch();
      setPasteText('');
    },
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">biometrics</h1>
        <Link to="/biometrics/breathing" className="text-sm text-muted-foreground hover:text-foreground">
          breathing →
        </Link>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        the AI uses this to time interventions. it is never the headline.
      </p>

      <section className="mt-8 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm font-medium">today</h2>
        {acute.data ? (
          acute.data.baselineN === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">no readings yet.</p>
          ) : (
            <>
              <p className="mt-1 text-sm">
                hrv today:{' '}
                <span className="font-mono">{acute.data.today !== null ? `${Math.round(acute.data.today)} ms` : '—'}</span>{' '}
                ·{' '}
                {acute.data.baselineMean !== null && (
                  <>
                    30-day baseline: <span className="font-mono">{Math.round(acute.data.baselineMean)} ± {acute.data.baselineSd?.toFixed(1) ?? 0} ms</span>{' '}
                  </>
                )}
              </p>
              {acute.data.strain ? (
                <p className="mt-2 rounded-[var(--radius)] bg-muted px-3 py-2 text-sm">
                  your nervous system is showing strain today. the coach will notice.
                </p>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">no acute strain detected.</p>
              )}
            </>
          )
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">…</p>
        )}
      </section>

      <section className="mt-6 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm font-medium">sources</h2>
        <ul className="mt-3 space-y-2 text-sm">
          {(summary.data?.sources ?? []).map((s) => (
            <li key={s.provider} className="flex justify-between">
              <span>{s.provider}</span>
              <span className="text-muted-foreground">
                {s.connectionType} · {s.lastSyncAt ? new Date(s.lastSyncAt).toLocaleString() : 'never synced'}
              </span>
            </li>
          ))}
          {summary.data?.sources.length === 0 && (
            <li className="text-muted-foreground">none connected yet.</li>
          )}
        </ul>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href="/api/biometrics/oura/start">
            <Button size="sm" variant="secondary">connect oura</Button>
          </a>
          <a href="/api/biometrics/whoop/start">
            <Button size="sm" variant="secondary">connect whoop</Button>
          </a>
          <a href="/api/biometrics/garmin/start">
            <Button size="sm" variant="secondary">connect garmin</Button>
          </a>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          apple health: use <code>scripts/apple-health-extract.ts</code> on your export.zip, then paste the JSON below.
        </p>
      </section>

      <section className="mt-6 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm font-medium">manual import</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          paste the JSON from any provider's extractor.
        </p>
        <textarea
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          rows={10}
          placeholder={PASTE_EXAMPLE}
          className="mt-3 w-full font-mono text-xs rounded-[var(--radius)] border border-input bg-background p-3"
        />
        {pasteError && <p className="mt-2 text-xs text-destructive">{pasteError}</p>}
        <div className="mt-3">
          <Button
            size="sm"
            disabled={importMutation.isPending || !pasteText.trim()}
            onClick={() => {
              setPasteError(null);
              try {
                const body = JSON.parse(pasteText);
                importMutation.mutate(body);
              } catch (err) {
                setPasteError((err as Error).message);
              }
            }}
          >
            {importMutation.isPending ? 'ingesting…' : 'ingest'}
          </Button>
          {importMutation.data && (
            <span className="ml-3 text-sm text-muted-foreground">
              ingested {importMutation.data.ingested} readings.
            </span>
          )}
        </div>
      </section>

      <section className="mt-6 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm font-medium">weekly digest</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          physiology over time, correlated with what you wrote.
        </p>
        <div className="mt-3">
          <Button size="sm" disabled={digest.isPending} onClick={() => digest.mutate()}>
            {digest.isPending ? 'building…' : 'build digest'}
          </Button>
        </div>
        {digest.data && (
          <article className="mt-4 text-sm whitespace-pre-wrap">{digest.data.digest}</article>
        )}
      </section>
    </section>
  );
}
